import { motion, useReducedMotion, useTransform } from "framer-motion";
import { Droplets, Flame, Inbox, MessageSquareText, Phone, Snowflake } from "lucide-react";
import { EASE } from "@/lib/lenis";
import { BrowserFrame } from "@/components/site/BrowserFrame";

const SERVICES = [
  [Flame, "Heating"],
  [Snowflake, "Cooling"],
  [Droplets, "Plumbing"],
];

const KestrelSite = () => (
  <div
    className="bg-[#10141B] text-white"
    role="img"
    aria-label="Sample homepage for Kestrel Heating & Plumbing, a fictional local business, with a call button, booking button, and service list"
  >
    <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-3 sm:px-8 sm:py-4">
      <div className="flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-md bg-[#FF7A1A]">
          <Flame className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="leading-tight">
          <p className="text-sm font-semibold">Kestrel</p>
          <p className="hidden text-xs text-white/60 sm:block">Heating & Plumbing</p>
        </div>
      </div>
      <ul className="hidden items-center gap-6 text-xs text-white/70 md:flex">
        <li>Services</li>
        <li>Service area</li>
        <li>About</li>
        <li>Contact</li>
      </ul>
      <span className="flex items-center gap-1.5 rounded-full bg-[#FF7A1A] px-3 py-1.5 text-xs font-semibold">
        <Phone className="h-3.5 w-3.5" aria-hidden="true" /> Call now
      </span>
    </div>
    <div className="grid items-center gap-6 px-4 py-6 sm:grid-cols-[1.1fr_1fr] sm:px-8 sm:py-10 lg:py-14">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#FFB27A]">Wayne · Totowa · Little Falls</p>
        <p className="mt-3 text-2xl font-semibold leading-[1.05] tracking-tight sm:text-3xl lg:text-[2.6rem]">
          Heating, cooling & plumbing for North Jersey homes.
        </p>
        <p className="mt-3 max-w-sm text-xs text-white/65 sm:text-sm">
          Book a visit online or tap to call. We'll confirm a time that works for you.
        </p>
        <div className="mt-5 flex flex-wrap gap-2 text-xs font-semibold">
          <span className="rounded-full bg-[#FF7A1A] px-3.5 py-2">Book a visit</span>
          <span className="rounded-full border border-white/20 px-3.5 py-2">Get a quote</span>
        </div>
      </div>
      <div className="relative aspect-[4/3] overflow-hidden rounded-xl">
        <img
          src="/images/hero-technician.jpg"
          alt=""
          width="1200"
          height="801"
          className="h-full w-full object-cover object-[50%_30%]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#10141B]/70 via-transparent to-transparent" />
        <span className="absolute bottom-3 left-3 rounded-full bg-white/90 px-2.5 py-1 text-xs font-medium text-[#14181F]">
          Now booking this week
        </span>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-2 border-t border-white/[0.06] px-4 py-5 sm:gap-4 sm:px-8 sm:py-6">
      {SERVICES.map(([Icon, label]) => (
        <div key={label} className="rounded-lg border border-white/[0.06] bg-white/[0.03] p-3 sm:p-4">
          <Icon className="h-4 w-4 text-[#FF7A1A]" aria-hidden="true" />
          <p className="mt-2 text-xs font-medium sm:text-sm">{label}</p>
          <p className="mt-1 hidden text-xs text-white/60 sm:block">Repairs & installs →</p>
        </div>
      ))}
    </div>
  </div>
);

const Chip = ({ icon: Icon, tone, title, sub, testId }) => (
  <div
    data-testid={testId}
    className="flex max-w-[230px] items-center gap-3 rounded-xl border border-white/10 bg-[#0F1218]/85 p-3 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.8)] backdrop-blur-xl sm:max-w-none sm:p-3.5"
  >
    <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${tone}`}>
      <Icon className="h-4 w-4" aria-hidden="true" />
    </span>
    <div className="min-w-0">
      <p className="truncate text-sm font-medium text-white">{title}</p>
      <p className="truncate text-xs text-[#A1A1AA]">{sub}</p>
    </div>
  </div>
);

export const HeroMockup = ({ progress }) => {
  const reduce = useReducedMotion();
  const rotateX = useTransform(progress, [0, 0.5], reduce ? [0, 0] : [18, 0]);
  const scale = useTransform(progress, [0, 0.5], reduce ? [1, 1] : [0.94, 1]);
  const y1 = useTransform(progress, [0, 1], reduce ? [0, 0] : [0, -90]);
  const y2 = useTransform(progress, [0, 1], reduce ? [0, 0] : [0, -170]);

  return (
    <div className="relative mx-auto mt-16 w-full max-w-6xl px-4 sm:mt-24 sm:px-8" style={{ perspective: 1800 }} data-testid="hero-browser-mockup">
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-[6%] mx-auto h-[70%] w-[85%] rounded-full bg-[radial-gradient(closest-side,rgba(47,107,255,0.5),rgba(34,184,200,0.2)_60%,transparent)] blur-2xl"
      />
      <motion.div initial={{ opacity: 0, y: 80 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.4, ease: EASE, delay: 0.55 }}>
        <motion.div style={{ rotateX, scale, transformOrigin: "50% 0%" }} className="hero-fade">
          <BrowserFrame url="yourbusiness.com">
            <KestrelSite />
          </BrowserFrame>
        </motion.div>
      </motion.div>
      <motion.div style={{ y: y1 }} className="absolute bottom-[22%] left-1 z-10 sm:left-2 xl:-left-4">
        <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, ease: EASE, delay: 1.3 }}>
          <Chip icon={Inbox} tone="bg-[#2F6BFF]/20 text-[#7EA2FF]" title="New quote request" sub="Furnace check · Wayne, NJ" testId="hero-chip-quote" />
        </motion.div>
      </motion.div>
      <motion.div style={{ y: y2 }} className="absolute right-1 top-[16%] z-10 hidden sm:right-2 sm:block xl:-right-4">
        <motion.div initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, ease: EASE, delay: 1.5 }}>
          <Chip icon={MessageSquareText} tone="bg-[#2DD4D4]/15 text-[#2DD4D4]" title="Missed call → text sent" sub="Auto-reply to the caller" testId="hero-chip-textback" />
        </motion.div>
      </motion.div>
    </div>
  );
};
